import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import { BuildingBeat } from "@/components/agents/setup/building-beat"
import { ActivateStep } from "@/components/agents/setup/lead-responder/activate-step"
import { HandoffStep } from "@/components/agents/setup/lead-responder/handoff-step"
import {
  type CrmSource,
  type LeadIntake,
  SourcesStep,
} from "@/components/agents/setup/lead-responder/sources-step"
import {
  STEP_ORDER,
  stepFor,
} from "@/components/agents/setup/lead-responder/steps"
import type { LeadResponderSetupData } from "@/components/agents/setup/lead-responder/types"
import { areaCodeFromPhone } from "@/components/agents/setup/receptionist/phone-utils"
import type {
  CalComEventType,
  CalendlyEventType,
  ConnectedCalendars,
} from "@/components/agents/setup/types"
import AppLayout from "@/layouts/app-layout"
import { agentPath, setupAgentPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface SetupAgent {
  uid: string
  name: string
  setup_step: string | null
  setup_data: Record<string, unknown>
  archetype: { uid: string | null; slug: string | null; name: string | null }
}

interface Props {
  agent: SetupAgent
  business_phone: string | null
  lead_intake: LeadIntake | null
  crm: CrmSource
  // IMP-419: the CRM lists/filters — a top-level deferred prop (undefined until
  // loaded after first paint), merged into `crm.lists` for the Sources step.
  crm_lists?: { id: string; name: string }[]
  // IMP-409: real calendar connection state + live event types for the Booking
  // & handoff step. Event types are deferred (loaded after first paint), so
  // undefined until they arrive.
  connected_calendars: ConnectedCalendars
  calendly_event_types?: CalendlyEventType[]
  cal_com_event_types?: CalComEventType[]
  // IMP-423: single gate for all texting surfaces. Off while texting is pulled
  // from self-serve; flip the LEAD_RESPONDER_TEXTING_ENABLED env to restore.
  texting_enabled: boolean
}

const STEP_COMPONENTS = [SourcesStep, HandoffStep, ActivateStep]

// IMP-461: the assistant builds the responder from the business profile
// first; the beat shows once (setup untouched) and advances on its own.
const BUILDING_LINES = [
  "Analyzing your business…",
  "Determining what makes a good lead…",
  "Getting ready to reach out to your leads…",
]

function defaultData(textingEnabled: boolean): LeadResponderSetupData {
  return {
    sources: [],
    crm_provider: "",
    crm_scope_value: "",
    // IMP-423: default call-first while texting is pulled from self-serve.
    channels: textingEnabled ? ["voice", "sms"] : ["voice"],
    // Seed the one universally-safe criterion so the step isn't a blank slate;
    // editable and removable like any other.
    booking_enabled: false,
    transfer_enabled: false,
    booking_calendar: "",
    booking_event_type: "",
    transfer_number: "",
    transfer_hours: [
      { day: "Monday", abbr: "Mon", open: true, from: "09:00", to: "17:00" },
      { day: "Tuesday", abbr: "Tue", open: true, from: "09:00", to: "17:00" },
      { day: "Wednesday", abbr: "Wed", open: true, from: "09:00", to: "17:00" },
      { day: "Thursday", abbr: "Thu", open: true, from: "09:00", to: "17:00" },
      { day: "Friday", abbr: "Fri", open: true, from: "09:00", to: "17:00" },
      { day: "Saturday", abbr: "Sat", open: false, from: "09:00", to: "17:00" },
      { day: "Sunday", abbr: "Sun", open: false, from: "09:00", to: "17:00" },
    ],
    speed_target: "asap",
    persistence: "standard",
    consent_attested: false,
    custom_instructions: "",
    sms_area_code: "",
    selected_phone_number: "",
  }
}

// Merge saved setup_data over the defaults so a returning user resumes their
// configuration; unknown/missing keys fall back to the default.
function initData(
  saved: Record<string, unknown>,
  businessPhone: string | null,
  textingEnabled: boolean,
): LeadResponderSetupData {
  const data = {
    ...defaultData(textingEnabled),
    ...(saved as Partial<LeadResponderSetupData>),
  }
  // IMP-399: prefill the preferred area code from the business profile phone,
  // matching the receptionist's Connect step — a value the user entered or
  // saved always wins over the prefill.
  if (!data.sms_area_code) {
    data.sms_area_code = areaCodeFromPhone(businessPhone)
  }
  return data
}

export default function LeadResponderSetup({
  agent,
  business_phone,
  lead_intake,
  crm,
  crm_lists,
  connected_calendars,
  calendly_event_types,
  cal_com_event_types,
  texting_enabled,
}: Props) {
  const [stepIndex, setStepIndex] = useState(() =>
    STEP_ORDER.indexOf(stepFor(agent.setup_step)),
  )
  const [data, setData] = useState<LeadResponderSetupData>(() =>
    initData(agent.setup_data ?? {}, business_phone, texting_enabled),
  )
  // IMP-345: drive the Launch button's loading state while the final
  // provision/launch request is in flight.
  const [launching, setLaunching] = useState(false)
  // IMP-538: bumped to re-fetch the number choices after the picked number
  // turned out to be taken at Launch.
  const [numbersRefresh, setNumbersRefresh] = useState(0)

  const isLast = stepIndex === STEP_ORDER.length - 1

  function handleUpdate(patch: Partial<LeadResponderSetupData>) {
    setData((prev) => ({ ...prev, ...patch }))
  }

  // Autosave the accumulated config to setup_data on every step move. On launch
  // (complete) the agent provisions its number and transitions draft ->
  // operational server-side, then we land on its dashboard.
  function persist(targetStep: string, complete = false) {
    router.patch(
      setupAgentPath({ id: agent.uid }),
      {
        setup_step: targetStep,
        complete,
        // LeadResponderSetupData is all Inertia-serialisable (scalars, string
        // arrays, and the transfer-hours object array); cast to satisfy the
        // PATCH body's index-signature requirement.
        setup_data: data as unknown as Record<
          string,
          | string
          | number
          | boolean
          | string[]
          | {
              day: string
              abbr: string
              open: boolean
              from: string
              to: string
            }[]
        >,
      },
      {
        preserveScroll: true,
        onStart: () => {
          if (complete) setLaunching(true)
        },
        onSuccess: () => {
          if (complete) router.visit(agentPath({ id: agent.uid }))
        },
        // Keep the spinner through the redirect on success; clear it if the
        // launch is rejected (e.g. an unconfigured handoff) so the user can fix
        // and retry.
        onError: (errors) => {
          setLaunching(false)
          // IMP-538: the picked number may be gone — clear it and offer a
          // fresh list to pick from.
          if ("retell_phone" in errors) {
            handleUpdate({ selected_phone_number: "" })
            setNumbersRefresh((n) => n + 1)
          }
        },
      },
    )
  }

  function handleNext() {
    if (isLast) {
      persist(STEP_ORDER[stepIndex], true)
      return
    }
    const next = stepIndex + 1
    persist(STEP_ORDER[next])
    setStepIndex(next)
  }

  function handleBack() {
    if (stepIndex === 0) return
    const prev = stepIndex - 1
    persist(STEP_ORDER[prev])
    setStepIndex(prev)
  }

  // IMP-360: a readiness issue on the Activate step links back to the step that
  // needs fixing. Persists the current config first (matching handleBack) so the
  // user doesn't lose edits when jumping.
  function goToStep(stepKey: string) {
    const idx = STEP_ORDER.indexOf(stepKey as (typeof STEP_ORDER)[number])
    if (idx < 0 || idx === stepIndex) return
    persist(STEP_ORDER[idx])
    setStepIndex(idx)
  }

  const StepComponent = STEP_COMPONENTS[stepIndex]
  const breadcrumbs: BreadcrumbItem[] = [
    { title: agent.name || "Lead Responder", href: "#" },
  ]
  const common = {
    stepNumber: stepIndex + 1,
    totalSteps: STEP_ORDER.length,
    data,
    onUpdate: handleUpdate,
    onNext: handleNext,
    onBack: handleBack,
    // IMP-423: every setup step keys its texting surfaces off this one gate.
    textingEnabled: texting_enabled,
  }
  // IMP-409: real calendar connection state + live event types for the Booking
  // & handoff step. A connect flow returns the user to this wizard URL.
  const calendars = {
    connectedCalendars: connected_calendars,
    calendlyEventTypes: calendly_event_types ?? [],
    calComEventTypes: cal_com_event_types ?? [],
    returnTo: typeof window === "undefined" ? "" : window.location.pathname,
  }

  // A fresh agent (no step persisted yet) opens with the building beat, which
  // persists the first step when it finishes so a return visit skips it.
  if (!agent.setup_step) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <Head title="Lead Responder setup" />
        <BuildingBeat
          title="Building your lead responder"
          lines={BUILDING_LINES}
          onDone={() => persist(STEP_ORDER[0])}
        />
      </AppLayout>
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Lead Responder setup" />

      <div className="h-app-viewport flex">
        <div className="min-w-0 flex-1">
          {StepComponent === ActivateStep ? (
            <ActivateStep
              {...common}
              agentUid={agent.uid}
              onGoToStep={goToStep}
              launching={launching}
              numbersRefreshKey={numbersRefresh}
            />
          ) : StepComponent === SourcesStep ? (
            // IMP-346/347: the Sources step shows the real per-agent intake
            // artifacts + live status, and the connected CRM list picker.
            <SourcesStep
              {...common}
              leadIntake={lead_intake ?? undefined}
              crm={{ ...crm, lists: crm_lists }}
            />
          ) : (
            // IMP-409: real calendar connection state + live event types wire
            // the Connect buttons.
            <HandoffStep {...common} calendars={calendars} />
          )}
        </div>
      </div>
    </AppLayout>
  )
}
